import _ from 'lodash';
import React, {useState, useEffect} from 'react';
import REST from 'go/requests/rest';
import {connectDynamic} from 'go/components/dynamic/dynamicComponent';
import {Segment, Header, Icon, Loader, Input, Pagination, Table} from 'semantic-ui-react';
import {FemaGoTable, Alert} from 'app-ui-componentlibrary';
import {
  compileContent,
  showDollarAmountWithDollar,
  formatLineNumber,
  formatDateMMDDYYYY,
} from 'common/utils';
import FundingOpportunityCommitmentModal from 'go/components/fo/FundingOpportunityCommitmentModal';
import FundingOpportunityContext from 'go/components/fo/FundingOpportunityContext';
import DynamicRadioField from 'componentLibrary/form/DynamicRadioField';

export const GrantCommitmentTable = (props) => {
  const [filteredData, setFilteredData] = useState([]);
  const [mergedFOCs, setMergedFOCs] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [activePage, setActivePage] = useState(1);
  const [isViewCommitmentModalOpen, setIsViewCommitmentModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [detailsData, setDetailsData] = useState([]);
  const {
    columns,
    header,
    headerSize,
    pageSize,
    entityDataPath,
    entityType,
    editMode,
    foCommitments,
    guidance,
    currentIndex,
  } = props;
  const REGIONS = ['HQ', 'I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X'];

  useEffect(() => {
    const loadCommitments = async () => {
      const grantCommitments = await REST.obligation
        .getAllObligationsByGrantId(props.entity.grantId)
        .then(REST.handler.handleResponse)
        .then((data) => {
          const obligations = data.obligation;
          const commitmentIds = obligations.map((obligation) => obligation.commitmentId);
          return _.uniq(commitmentIds);
        })
        .then((ids) => {
          return REST.commitment
            .getListCommitmentDetails(ids)
            .then(REST.handler.handleResponse)
            .then((data) => data.commitmentDetailsResponse);
        });
      formatCommitments(grantCommitments);
    };
    loadCommitments();
  }, []);

  const formatCommitments = (commitments) => {
    let commitmentsFormatted = [];

    _.forEach(commitments, (commitmentToFormat) => {
      let commitmentRegion = null;
      for (let i = 0; i < foCommitments.length; i++) {
        if (
          foCommitments[i].commitmentNumbers === commitmentToFormat.commitmentNumberId
        ) {
          commitmentRegion = foCommitments[i].commitmentRegion;
          break;
        }
      }

      let newCommitment = {
        ...commitmentToFormat,
        commitmentNumberIdAndSeq: `${
          commitmentToFormat.commitmentNumberId
        }-${formatLineNumber(commitmentToFormat.commitmentLineNumber)}`,
        commitmentRegion: REGIONS[commitmentRegion],
        commitmentAvailableAmount: showDollarAmountWithDollar(
          commitmentToFormat.commitmentAvailableAmount
        ),
        commitmentLineNumber: `${formatLineNumber(
          commitmentToFormat.commitmentLineNumber
        )}`,
        commitmentAmount: showDollarAmountWithDollar(commitmentToFormat.commitmentAmount),
        commitmentStartDate: formatDateMMDDYYYY(
          new Date(commitmentToFormat.commitmentStartDate).toISOString()
        ),
        commitmentEndDate: formatDateMMDDYYYY(
          new Date(commitmentToFormat.commitmentEndDate).toISOString()
        ),
      };
      commitmentsFormatted.push(newCommitment);
    });
    const commitmentsSortedAndFormatted = _.sortBy(commitmentsFormatted, (item) => {
      return item.commitmentNumberIdAndSeq;
    });
    setFilteredData(commitmentsSortedAndFormatted);
    setMergedFOCs(commitmentsSortedAndFormatted);
    setLoading(false);

    return commitmentsSortedAndFormatted;
  };

  const handleSearch = (e) => {
    const searchTerm = e.target.value;
    setSearchTerm(searchTerm);

    const filtered = _.filter(mergedFOCs, (item) =>
      _.some(item, (value) =>
        _.includes(_.toLower(_.toString(value)), _.toLower(searchTerm))
      )
    );

    setFilteredData(filtered);
    setActivePage(1);
  };

  const handlePageChange = (e, {activePage}) => {
    setActivePage(activePage);
  };

  const openViewCommitmentModel = (detailsData) => {
    setDetailsData(detailsData);
    setIsViewCommitmentModalOpen(true);
  };

  const closeModal = () => {
    setIsViewCommitmentModalOpen(false);
  };

  const renderTable = () => {
    const keys = _.keys(columns);
    const pageIndex = activePage - 1;
    const startIndex = pageIndex * pageSize;
    const endIndex = startIndex + pageSize;
    const paginatedData = _.slice(filteredData, startIndex, endIndex);
    const recordCount = paginatedData.length;
    const summary = header + '#' + currentIndex;
    if (editMode && recordCount == 1 && props.data === undefined) {
      let newData = paginatedData[0].commitmentNumberId;
      props.saveEntityData(`${props.entityDataPath}`, `${props.entityType}`, newData);
    }
    return (
      <FemaGoTable
        columns={keys.length}
        withForm={editMode}
        summary={summary}
        name={summary}
      >
        <FemaGoTable.Header>
          <FemaGoTable.Row>
            {_.map(keys, (k) => (
              <FemaGoTable.HeaderCell key={k}>
                {k === 'Radio' ? '' : k}
              </FemaGoTable.HeaderCell>
            ))}
          </FemaGoTable.Row>
        </FemaGoTable.Header>
        <FemaGoTable.Body>
          {_.map(paginatedData, (row, i) => {
            return (
              <FemaGoTable.Row key={i}>
                {_.map(keys, (k, j) => {
                  const curr = columns[k];
                  if (
                    k === 'Commitment number - sequence number' ||
                    k === 'Obligation line - sequence number'
                  ) {
                    return (
                      <FemaGoTable.TextCell key={j}>
                        {row.commitmentNumberIdAndSeq}
                      </FemaGoTable.TextCell>
                    );
                  }
                  if (k === 'Available amount') {
                    return (
                      <FemaGoTable.NumberCell type="dollar" key={j}>
                        {row.commitmentAvailableAmount}
                      </FemaGoTable.NumberCell>
                    );
                  }
                  if (k === 'Region') {
                    return (
                      <FemaGoTable.TextCell key={j}>
                        {row.commitmentRegion}
                      </FemaGoTable.TextCell>
                    );
                  }
                  if (k === 'Fiscal year') {
                    return (
                      <FemaGoTable.TextCell key={j}>
                        {row.commitmentBudgetFiscalYear}
                      </FemaGoTable.TextCell>
                    );
                  }
                  if (k === 'ACCS code') {
                    return (
                      <FemaGoTable.TextCell key={j}>{row.accsline}</FemaGoTable.TextCell>
                    );
                  }
                  if (k === 'Radio') {
                    return (
                      <Table.Cell className="no-padding row--horizontally-center">
                        <DynamicRadioField
                          ariaLabelledBy={row.commitmentNumberId}
                          options={[{label: '', value: row.commitmentNumberId}]}
                          entityDataPath={entityDataPath}
                          entityType={entityType}
                          isHidden={!editMode && row.commitmentNumberId !== props.data}
                          disabled={!editMode}
                        />
                      </Table.Cell>
                    );
                  }
                  return (
                    <FemaGoTable.TextCell key={j}>
                      {compileContent(curr, {row}, true)}
                    </FemaGoTable.TextCell>
                  );
                })}
              </FemaGoTable.Row>
            );
          })}
        </FemaGoTable.Body>
      </FemaGoTable>
    );
  };

  return (
    <FundingOpportunityContext.Provider value={props.context.fundingOpportunityData}>
      <Segment>
        <Header content={header} as={headerSize || 'h2'} />
        {editMode && (
          <div className="search-bar commitment-search-bar">
            <Input
              icon={<Icon name="search" />}
              placeholder="Search..."
              value={searchTerm}
              onChange={handleSearch}
            />
          </div>
        )}

        {loading ? (
          <div
            className="margin-top"
            style={{minHeight: '63px', display: 'flex', alignItems: 'center'}}
          >
            <Loader inline="centered" size="large" active>
              Loading...
            </Loader>
          </div>
        ) : (
          <>
            {editMode && (
              <Alert header="" type="info" dismissable={false}>
                {guidance}
              </Alert>
            )}
            {!_.isEmpty(mergedFOCs) && renderTable()}
            {filteredData.length > pageSize && (
              <Pagination
                activePage={activePage}
                totalPages={Math.ceil(filteredData.length / pageSize)}
                onPageChange={handlePageChange}
              />
            )}
          </>
        )}
        <FundingOpportunityCommitmentModal
          isOpen={isViewCommitmentModalOpen}
          isEditable={false}
          detailsData={detailsData}
          closeModal={closeModal}
        />
      </Segment>
    </FundingOpportunityContext.Provider>
  );
};

export default connectDynamic(GrantCommitmentTable);
